// Per-project demo state: has the presenter run the simulated assessment, and
// which students' grades have been revealed so far. Persisted in localStorage
// so the state survives navigation and a page reload during a demo; "Reset
// assessment" clears it for a fresh run.
//
// Keyed by the ArcDisk project id (the fixture key), not the Mosaico card id.
import { useSyncExternalStore } from 'react'

export type DemoStatus = 'ungraded' | 'grading' | 'graded'

export interface ProjectDemoState {
  status: DemoStatus
  // student_ids whose grades are visible, in the order they were revealed
  revealed: number[]
  startedAt?: number
  finishedAt?: number
}

const STORAGE_KEY = 'arcdisk.demo.state.v1'
const EMPTY: ProjectDemoState = Object.freeze({ status: 'ungraded', revealed: [] }) as ProjectDemoState

type Store = Record<string, ProjectDemoState>

const listeners = new Set<() => void>()
let store: Store | null = null

function load(): Store {
  if (store) return store
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null
    store = raw ? (JSON.parse(raw) as Store) : {}
  } catch {
    store = {}
  }
  return store
}

function persist() {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(load()))
  } catch {
    // private mode / quota: the in-memory copy still drives the UI
  }
}

function emit() {
  listeners.forEach(l => l())
}

export function getProjectDemoState(projectId: string): ProjectDemoState {
  return load()[projectId] ?? EMPTY
}

export function setProjectDemoState(projectId: string, patch: Partial<ProjectDemoState>) {
  const s = load()
  s[projectId] = { ...(s[projectId] ?? EMPTY), ...patch }
  persist()
  emit()
}

export function revealStudent(projectId: string, studentId: number) {
  const cur = getProjectDemoState(projectId)
  if (cur.revealed.includes(studentId)) return
  setProjectDemoState(projectId, { revealed: [...cur.revealed, studentId] })
}

export function resetProjectDemoState(projectId: string) {
  const s = load()
  delete s[projectId]
  persist()
  emit()
}

export function resetAllDemoState() {
  store = {}
  persist()
  emit()
}

/** True when this student's grades are visible in ANY demo project. */
export function isStudentRevealed(studentId: number): boolean {
  return Object.values(load()).some(p => p.revealed.includes(studentId))
}

export function subscribeDemoState(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

export function useProjectDemoState(projectId: string): ProjectDemoState {
  return useSyncExternalStore(subscribeDemoState, () => getProjectDemoState(projectId))
}

/** Re-renders when any project's demo state changes; returns the whole store. */
export function useAllDemoState(): Store {
  return useSyncExternalStore(subscribeDemoState, () => load())
}
